import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  Package, Plus, Search, Edit3, Power, PowerOff, Tag,
  Trash2, History, X, GripVertical,
} from 'lucide-react';

/**
 * Product Catalog admin — Stage 1
 *
 * Lives inside the Rate Catalog page as a new tab. Owner/Admin only
 * (gated server-side via rates_manage). Lets admins:
 *   • Manage categories (add, rename, delete when empty).
 *   • Add, edit, activate / deactivate products.
 *   • Each product carries its default description, unit, rate, GST,
 *     product-specific terms, and internal notes.
 */

const UNIT_LABELS = {
  per_month: 'Per month', per_week: 'Per week', per_day: 'Per day',
  per_hour: 'Per hour', per_post: 'Per post', per_item: 'Per item',
  one_time: 'One-time',
};
const UNIT_ORDER = ['per_month', 'per_week', 'per_day', 'per_hour',
  'per_post', 'per_item', 'one_time'];

function fmtINR(n) {
  if (n == null || n === '') return '—';
  try { return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`; }
  catch { return `₹${n}`; }
}

function emptyProduct() {
  return {
    name: '', category: 'Digital Marketing',
    default_description: '', unit_type: 'per_month',
    default_rate: 0, gst_pct: 18,
    product_terms: [],
    internal_notes: '',
    active: true,
  };
}

export default function ProductCatalogAdmin() {
  const { isOwner } = useAuth();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [showInactive, setShowInactive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showCatModal, setShowCatModal] = useState(false);
  const [editing, setEditing] = useState(null); // product or null
  const [historyFor, setHistoryFor] = useState(null); // product id or null

  const load = async () => {
    setLoading(true);
    try {
      const [cats, list] = await Promise.all([
        api.get('/catalog/categories'),
        api.get('/catalog/products', {
          params: {
            q, category: filterCat === 'all' ? '' : filterCat,
            include_inactive: showInactive, size: 200,
          },
        }),
      ]);
      setCategories(cats.data || []);
      setProducts(list.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load catalog');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, filterCat, showInactive]);

  const openNew = () => setEditing({ ...emptyProduct() });
  const openEdit = (p) => setEditing({ ...p });

  const toggleActive = async (p) => {
    try {
      await api.post(`/catalog/products/${p.id}/toggle-active`, { active: !p.active });
      toast.success(p.active ? 'Marked inactive' : 'Marked active');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update'); }
  };

  const grouped = useMemo(() => {
    const by = {};
    for (const p of products) {
      const c = p.category || 'Other';
      (by[c] ||= []).push(p);
    }
    return Object.entries(by).sort((a, b) => a[0].localeCompare(b[0]));
  }, [products]);

  return (
    <div className="space-y-4" data-testid="product-catalog-admin">
      <Card className="shine-card p-4 rounded-xl">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="h-4 w-4 text-ink-2 absolute left-2.5 top-2.5" />
            <Input placeholder="Search products or descriptions"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   className="pl-8" data-testid="pcat-search" />
          </div>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-[200px]" data-testid="pcat-filter-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map(c => (
                <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 text-xs">
            <Switch id="pcat-inactive" checked={showInactive}
                    onCheckedChange={setShowInactive}
                    data-testid="pcat-show-inactive" />
            <Label htmlFor="pcat-inactive" className="cursor-pointer">
              Include inactive
            </Label>
          </div>
          <Button variant="outline" onClick={() => setShowCatModal(true)}
                  data-testid="pcat-categories-btn">
            <Tag className="h-4 w-4 mr-1" /> Categories
          </Button>
          <Button onClick={openNew} data-testid="pcat-add-product-btn">
            <Plus className="h-4 w-4 mr-1" /> Add product
          </Button>
        </div>
      </Card>

      {loading ? (
        <Card className="shine-card p-6 rounded-xl text-sm text-ink-2">Loading…</Card>
      ) : grouped.length === 0 ? (
        <Card className="shine-card p-10 rounded-xl text-center" data-testid="pcat-empty">
          <Package className="h-8 w-8 text-ink-2 mx-auto mb-2" />
          <div className="text-sm font-semibold text-ink">No products yet</div>
          <div className="text-xs text-ink-2 mt-1">
            Add your first product to let Sales build Product-Catalog proposals.
          </div>
        </Card>
      ) : (
        grouped.map(([cat, items]) => (
          <Card key={cat} className="shine-card rounded-xl overflow-hidden">
            <div className="px-4 py-2 border-b border-ink/10 bg-slate-50/50 text-xs font-semibold uppercase text-ink-2">
              {cat} <span className="text-ink-2/70">· {items.length}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-ink-2 uppercase border-b border-ink/10">
                    <th className="py-2 px-3">Product</th>
                    <th className="py-2 px-3">Unit</th>
                    <th className="py-2 px-3 text-right">Default rate</th>
                    <th className="py-2 px-3 text-right">GST</th>
                    <th className="py-2 px-3">Terms</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(p => (
                    <tr key={p.id} className="border-b border-ink/5 hover:bg-slate-50/40">
                      <td className="py-2 px-3">
                        <div className="font-semibold text-ink">{p.name}</div>
                        {p.default_description && (
                          <div className="text-[11px] text-ink-2 line-clamp-2 whitespace-pre-wrap">
                            {p.default_description}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 text-xs text-ink-2">
                        {UNIT_LABELS[p.unit_type] || p.unit_type}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-ink">
                        {fmtINR(p.default_rate)}
                      </td>
                      <td className="py-2 px-3 text-right text-xs text-ink-2">
                        {p.gst_pct}%
                      </td>
                      <td className="py-2 px-3 text-xs text-ink-2">
                        {(p.product_terms || []).length} clause{(p.product_terms || []).length === 1 ? '' : 's'}
                      </td>
                      <td className="py-2 px-3">
                        {p.active ? (
                          <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">Active</Badge>
                        ) : (
                          <Badge variant="outline" className="text-ink-2">Inactive</Badge>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(p)}
                                  data-testid={`pcat-edit-${p.id}`}>
                            <Edit3 className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => toggleActive(p)}
                                  data-testid={`pcat-toggle-${p.id}`}>
                            {p.active ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setHistoryFor(p.id)}
                                  data-testid={`pcat-history-${p.id}`}>
                            <History className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ))
      )}

      {editing && (
        <ProductEditor
          product={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {showCatModal && (
        <CategoriesModal
          categories={categories}
          onClose={() => setShowCatModal(false)}
          onChanged={load}
        />
      )}
      {historyFor && (
        <HistoryModal pid={historyFor} onClose={() => setHistoryFor(null)} />
      )}
    </div>
  );
}


/* ─────────────────────── Product editor ─────────────────────── */
function ProductEditor({ product, categories, onClose, onSaved }) {
  const isNew = !product.id;
  const [form, setForm] = useState({ ...product });
  const [busy, setBusy] = useState(false);
  const terms = form.product_terms || [];

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addTerm = () => setField('product_terms',
    [...terms, { id: crypto.randomUUID(), text: '', order: terms.length }]);
  const setTerm = (idx, text) => {
    const next = [...terms];
    next[idx] = { ...next[idx], text };
    setField('product_terms', next);
  };
  const removeTerm = (idx) => {
    const next = [...terms];
    next.splice(idx, 1);
    setField('product_terms', next);
  };
  const moveTerm = (idx, dir) => {
    const next = [...terms];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    setField('product_terms', next);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Product name is required');
    if (!form.unit_type) return toast.error('Pick a unit type');
    if (form.default_rate == null || form.default_rate < 0)
      return toast.error('Default rate must be ≥ 0');
    setBusy(true);
    try {
      const body = {
        ...form,
        default_rate: Number(form.default_rate),
        gst_pct: Number(form.gst_pct ?? 18),
        product_terms: terms.filter(t => (t.text || '').trim()).map((t, i) => ({
          id: t.id, text: t.text.trim(), order: i,
        })),
      };
      if (isNew) await api.post('/catalog/products', body);
      else await api.patch(`/catalog/products/${form.id}`, body);
      toast.success(isNew ? 'Product added' : 'Product saved');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="pcat-editor-title">
            {isNew ? 'Add product' : 'Edit product'}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Name <span className="text-rose-600">*</span></Label>
            <Input value={form.name} onChange={(e) => setField('name', e.target.value)}
                   placeholder="e.g. Instagram Reel" data-testid="pcat-field-name" />
          </div>
          <div>
            <Label>Category <span className="text-rose-600">*</span></Label>
            <Select value={form.category} onValueChange={(v) => setField('category', v)}>
              <SelectTrigger data-testid="pcat-field-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(c => (
                  <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Unit type <span className="text-rose-600">*</span></Label>
            <Select value={form.unit_type} onValueChange={(v) => setField('unit_type', v)}>
              <SelectTrigger data-testid="pcat-field-unit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {UNIT_ORDER.map(k => (
                  <SelectItem key={k} value={k}>{UNIT_LABELS[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Default rate (₹) <span className="text-rose-600">*</span></Label>
            <Input type="number" min="0" step="0.01" value={form.default_rate}
                   onChange={(e) => setField('default_rate', e.target.value)}
                   data-testid="pcat-field-rate" />
          </div>
          <div>
            <Label>GST percent</Label>
            <Input type="number" min="0" max="28" step="0.01" value={form.gst_pct}
                   onChange={(e) => setField('gst_pct', e.target.value)}
                   data-testid="pcat-field-gst" />
          </div>
          <div className="col-span-2">
            <Label>Default description (shown on the proposal and PDF)</Label>
            <Textarea rows={4} value={form.default_description}
                      onChange={(e) => setField('default_description', e.target.value)}
                      placeholder="Multi-line. Use line breaks for bullets."
                      data-testid="pcat-field-description" />
          </div>
          <div className="col-span-2">
            <Label>Internal notes <span className="text-ink-2 font-normal text-xs">(never shown to clients)</span></Label>
            <Textarea rows={2} value={form.internal_notes}
                      onChange={(e) => setField('internal_notes', e.target.value)}
                      data-testid="pcat-field-internal" />
          </div>
          <div className="col-span-2 space-y-2">
            <div className="flex items-center justify-between">
              <Label>Product terms</Label>
              <Button type="button" size="sm" variant="outline" onClick={addTerm}
                      data-testid="pcat-add-term-btn">
                <Plus className="h-3 w-3 mr-1" /> Add clause
              </Button>
            </div>
            {terms.length === 0 && (
              <div className="text-xs text-ink-2 italic">No product-specific terms yet.</div>
            )}
            {terms.map((t, i) => (
              <div key={t.id || i} className="flex items-start gap-2">
                <div className="flex flex-col pt-2">
                  <button type="button" className="text-ink-2 hover:text-ink"
                          onClick={() => moveTerm(i, -1)}
                          data-testid={`pcat-term-up-${i}`}>▲</button>
                  <button type="button" className="text-ink-2 hover:text-ink"
                          onClick={() => moveTerm(i, 1)}
                          data-testid={`pcat-term-down-${i}`}>▼</button>
                </div>
                <Textarea rows={2} value={t.text}
                          onChange={(e) => setTerm(i, e.target.value)}
                          data-testid={`pcat-term-input-${i}`} />
                <Button type="button" size="sm" variant="ghost"
                        onClick={() => removeTerm(i)}
                        data-testid={`pcat-term-remove-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <Switch id="pcat-active" checked={form.active}
                    onCheckedChange={(v) => setField('active', v)}
                    data-testid="pcat-field-active" />
            <Label htmlFor="pcat-active" className="cursor-pointer">
              Active (visible to Sales on new proposals)
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="pcat-editor-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="pcat-editor-save">
            {busy ? 'Saving…' : (isNew ? 'Create product' : 'Save changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─────────────────────── Categories modal ─────────────────────── */
function CategoriesModal({ categories, onClose, onChanged }) {
  const [newName, setNewName] = useState('');
  const [edit, setEdit] = useState({}); // {id: name}
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.post('/catalog/categories', { name: newName.trim() });
      setNewName('');
      toast.success('Category added');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add'); }
    finally { setBusy(false); }
  };
  const rename = async (c) => {
    const name = (edit[c.id] ?? c.name).trim();
    if (!name || name === c.name) return setEdit({ ...edit, [c.id]: undefined });
    try {
      await api.patch(`/catalog/categories/${c.id}`, { name });
      toast.success('Renamed');
      setEdit({ ...edit, [c.id]: undefined });
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not rename'); }
  };
  const remove = async (c) => {
    if (c.is_default) return toast.error('Default categories cannot be deleted');
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try {
      await api.delete(`/catalog/categories/${c.id}`);
      toast.success('Deleted');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Product categories</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input placeholder="New category name" value={newName}
                   onChange={(e) => setNewName(e.target.value)}
                   data-testid="pcat-newcat-name" />
            <Button onClick={add} disabled={busy || !newName.trim()}
                    data-testid="pcat-newcat-add">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {categories.map(c => (
              <div key={c.id} className="flex items-center gap-2">
                <Input value={edit[c.id] ?? c.name}
                       onChange={(e) => setEdit({ ...edit, [c.id]: e.target.value })}
                       onBlur={() => edit[c.id] !== undefined && rename(c)}
                       disabled={c.is_default}
                       data-testid={`pcat-cat-input-${c.id}`} />
                {c.is_default && <Badge variant="outline" className="text-ink-2 text-[10px]">default</Badge>}
                {!c.is_default && isOwner && (
                  <Button variant="ghost" size="sm" onClick={() => remove(c)}
                          data-testid={`pcat-cat-remove-${c.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─────────────────────── History modal ─────────────────────── */
function HistoryModal({ pid, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/catalog/products/${pid}/history`).then(r => setRows(r.data))
      .catch(() => setRows([]));
  }, [pid]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Product history</DialogTitle>
        </DialogHeader>
        {!rows ? (
          <div className="text-sm text-ink-2">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-ink-2">No history yet.</div>
        ) : (
          <ul className="space-y-2 text-sm">
            {rows.map(r => (
              <li key={r.id} className="border-l-2 border-ink/10 pl-3">
                <div className="font-semibold text-ink capitalize">{r.action}</div>
                <div className="text-xs text-ink-2">
                  {r.at && new Date(r.at).toLocaleString()}
                  {r.actor_name ? ` · ${r.actor_name} (${r.actor_role})` : ''}
                </div>
                {r.diff_reason && (
                  <div className="text-xs text-ink-2 mt-1 italic">{r.diff_reason}</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
